
// export default LoginScreen;
import React, { useState, useEffect, useMemo } from "react";
import {
    View, Text, TextInput, Button, StyleSheet, TouchableOpacity, ScrollView, Image, ActivityIndicator
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useAuth } from "./AuthContext";
import { FASTAPI_BASE_URL, REACT_APP_GOOGLE_CLIENT_ID } from "./config";
import { useRoute } from "@react-navigation/native";
import { normalizeEmail } from "./utils/email";

const API_LOGIN = `${FASTAPI_BASE_URL}/login`;
const API_DAVINCI_EMAIL_REGISTER = `${FASTAPI_BASE_URL}/signup/davinci-email-register`;
import { useTranslation } from "./i18n/useTranslation";
import { isNetworkFailure } from "./utils/networkErrors";
import { useRef } from "react";

// BDD 2026-06-03 — Partner co-branded landing
// When a student arrives via a partner QR (e.g. ?school=davinci or ?code=DAVINCI-...),
// show a co-branded hero (partner logo × Saras-3D logo) above the normal login form.
// To add the real partner logo: drop the PNG in ../assets and set `logo:` below.
const PARTNER_BRANDS = {
    davinci: {
        name: "Da Vinci",
        loginTitle: "Login for Da Vinci Science Challenge",
        logo: require("../assets/images/davinci.png"),
    },
};

// BDD 2026-06-04 — Da Vinci exhibit 3D models that float around the login card.
// GLBs are bundled assets (metro.config.js registers the .glb extension), so
// require() resolves to a URL served by the bundler. Each entry maps to a
// floating-position class (corners/sides around the centered login card).
const DAVINCI_MODELS = [
    { mod: require("../assets/models/Heart.glb"), cls: "dv-tl", glow: "#fb7185" },
    { mod: require("../assets/models/Pendulum_waves.glb"), cls: "dv-bl", glow: "#a78bfa" },
    { mod: require("../assets/models/Graphite.glb"), cls: "dv-tr", glow: "#34d399" },
    { mod: require("../assets/models/Brain.glb"), cls: "dv-br", glow: "#f472b6" },
];

// Resolve a require()'d asset to a URL string across RN-web / Expo variants.
const resolveModelUri = (mod) => {
    if (!mod) return "";
    if (typeof mod === "string") return mod;
    if (mod.uri) return mod.uri;
    try {
        const s = Image.resolveAssetSource(mod);
        if (s && s.uri) return s.uri;
    } catch {
        // ignore
    }
    return "";
};

const detectPartnerBrand = () => {
    try {
        if (typeof window === "undefined") return null;
        const p = new URL(window.location.href).searchParams;
        const code = (p.get("code") || "").toLowerCase();
        const school = (p.get("school") || "").toLowerCase();
        if (school === "davinci" || code.startsWith("davinci")) {
            return PARTNER_BRANDS.davinci;
        }
    } catch {
        // ignore and fall through to default branding
    }
    return null;
};

// const LoginScreen = () => {

const DEFAULT_LOGIN_ERROR = "Login Failed! Check your credentials.";

const extractLoginErrorMessage = (payload) => {
    const detail = payload?.detail;

    if (Array.isArray(detail)) {
        const validationMessage = detail
            .map((item) => item?.msg || item?.message || item?.detail || "")
            .filter(Boolean)
            .join("; ");
        if (validationMessage) {
            return validationMessage;
        }
    }

    if (detail && typeof detail === "object") {
        const directMessage =
            detail.message ||
            detail.detail ||
            detail.error ||
            payload?.message;
        if (typeof directMessage === "string" && directMessage.trim()) {
            return directMessage.trim();
        }
    }

    const messageCandidates = [
        typeof detail === "string" ? detail : "",
        payload?.message,
        payload?.error,
        payload?.raw,
    ];

    const message = messageCandidates.find(
        (value) => typeof value === "string" && value.trim()
    );

    return message ? message.trim() : "";
};

const parseLoginResponse = async (response) => {
    let rawText = "";
    try {
        rawText = await response.text();
    } catch (readError) {
        console.error("Login response read error:", readError);
    }

    let data = {};
    if (rawText) {
        try {
            data = JSON.parse(rawText);
        } catch {
            data = { raw: rawText };
        }
    }

    return { data, rawText };
};

const formatNetworkErrorMessage = (error) => {
    const message = String(error?.message || "").trim();
    if (message) {
        return message;
    }
    return DEFAULT_LOGIN_ERROR;
};

export default function LoginScreen() {
    // ✔️ Move it _inside_ the component
    // You’ll probably want to pass your user‑selected language here instead of hard‑coding “en”
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState("");
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [davinciPasswordMode, setDavinciPasswordMode] = useState(false);
    const [davinciExistingUser, setDavinciExistingUser] = useState(false);
    const [davinciSubmitting, setDavinciSubmitting] = useState(false);
    const [davinciMessage, setDavinciMessage] = useState("");

    // 1️⃣ local state for the saved language code
    const [lang, setLang] = useState("en");
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const navigation = useNavigation();
    const { login } = useAuth();

    const route = useRoute();
    const prefill = route?.params?.prefillEmail || "";
    const toast = route?.params?.toast || "";
    const [banner, setBanner] = useState("");
    const googleButtonRef = useRef(null);
    const verificationRedirectStartedRef = useRef(false);
    const autoLoginHandledRef = useRef(false);
    const canSubmitLogin = username.trim().length > 0 && password.trim().length > 0;
    // BDD 2026-06-03 — partner co-branding (Da Vinci, etc.) from the QR URL
    const partner = useMemo(detectPartnerBrand, []);
    // BDD 2026-06-04 — interactive 3D exhibit showcase (Da Vinci)
    const modelShowcaseRef = useRef(null);

    useEffect(() => { if (toast) setBanner(toast); }, [toast]);

    useEffect(() => {
        const loadStoredCredentials = async () => {
            const storedUsername = await AsyncStorage.getItem("remembered_username");
            const storedPassword = await AsyncStorage.getItem("remembered_password");
            if (storedUsername && storedPassword) {
                setUsername(storedUsername);
                setPassword(storedPassword);
                setRememberMe(true);
            }
            const saved = await AsyncStorage.getItem("secondary_language_id");
            // if they chose "auto", default to English
            setLang((saved === "auto" || !saved) ? "en" : saved);

        };
        loadStoredCredentials();
    }, []);

    useEffect(() => {
        if (typeof window === "undefined" || verificationRedirectStartedRef.current) return;
        try {
            const params = new URL(window.location.href).searchParams;
            const verifyToken = (params.get("verify_token") || "").trim();
            if (!verifyToken) return;
            verificationRedirectStartedRef.current = true;
            const verifyUrl = `${FASTAPI_BASE_URL}/signup/verify-email?token=${encodeURIComponent(verifyToken)}`;
            window.location.replace(verifyUrl);
        } catch (e) {
            console.error("Verification redirect failed:", e);
        }
    }, []);

    useEffect(() => {
        if (typeof window === "undefined" || autoLoginHandledRef.current) return;
        const finishAutoLogin = async () => {
            try {
                const params = new URL(window.location.href).searchParams;
                const shouldAutoLogin = params.get("autologin") === "1";
                const accessToken = (params.get("access_token") || "").trim();
                const refreshToken = (params.get("refresh_token") || accessToken).trim();
                const email = normalizeEmail(params.get("email") || "");
                if (!shouldAutoLogin || !accessToken || !email) return;

                autoLoginHandledRef.current = true;
                setLoading(true);
                setIsLoggingIn(true);
                setError("");

                await AsyncStorage.setItem("auth_token", accessToken);
                await AsyncStorage.setItem("refresh_token", refreshToken);
                await AsyncStorage.setItem("previous_access_token", accessToken);

                await login(accessToken, {
                    email,
                    name: email.split("@")[0],
                    role: "student",
                });

                try {
                    const cleanUrl = new URL(window.location.href);
                    [
                        "autologin",
                        "access_token",
                        "refresh_token",
                        "email",
                        "plan_code",
                        "goto",
                        "verify_token",
                    ].forEach((key) => cleanUrl.searchParams.delete(key));
                    window.history.replaceState({}, document.title, cleanUrl.toString());
                } catch (_) {}
            } catch (e) {
                console.error("Verification auto-login failed:", e);
                setError("Verification succeeded, but auto-login could not be completed.");
                autoLoginHandledRef.current = false;
            } finally {
                setLoading(false);
                setIsLoggingIn(false);
            }
        };

        finishAutoLogin();
    }, [login]);

    useEffect(() => {
        const loadGoogleScript = () => {
            const script = document.createElement("script");
            script.src = "https://accounts.google.com/gsi/client";
            script.async = true;
            script.defer = true;
            script.onload = initializeGoogle;
            document.body.appendChild(script);
        };

        const initializeGoogle = () => {
            if (window.google && googleButtonRef.current) {
                window.google.accounts.id.initialize({
                    client_id: REACT_APP_GOOGLE_CLIENT_ID,
                    callback: handleGoogleResponse,
                });

                window.google.accounts.id.renderButton(
                    googleButtonRef.current,
                    {
                        theme: "outline",
                        size: "large",
                        width: 250,
                    }
                );
            }
        };

        loadGoogleScript();
    }, []);

    // BDD 2026-06-04 — load <model-viewer> + float the Da Vinci 3D exhibit
    // models around the login card (corners/sides), no labels, responsive.
    useEffect(() => {
        if (!partner || typeof document === "undefined") return;

        // Fun rounded display font for the challenge headline (kid-friendly).
        if (!document.getElementById("dv-font-link")) {
            const link = document.createElement("link");
            link.id = "dv-font-link";
            link.rel = "stylesheet";
            link.href = "https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&display=swap";
            document.head.appendChild(link);
        }

        // One-time stylesheet: animated background, glowing models, floating
        // layout, entrance pops, and graceful narrow-screen collapse.
        if (!document.getElementById("dv-showcase-style")) {
            const styleEl = document.createElement("style");
            styleEl.id = "dv-showcase-style";
            styleEl.textContent = `
                /* Let the animated page background show through the app shell */
                html, body, #root { background: transparent !important; }
                /* Animated aurora background (light + colorful, logos stay readable) */
                body::before {
                    content:""; position:fixed; inset:-25%; z-index:-2;
                    background:
                        radial-gradient(40% 40% at 20% 28%, rgba(96,165,250,0.55), transparent 60%),
                        radial-gradient(42% 42% at 82% 22%, rgba(167,139,250,0.50), transparent 60%),
                        radial-gradient(48% 48% at 66% 82%, rgba(244,114,182,0.40), transparent 60%),
                        radial-gradient(42% 42% at 26% 80%, rgba(45,212,191,0.45), transparent 60%),
                        linear-gradient(120deg,#eef4ff,#f3efff,#eafcff);
                    background-size: 200% 200%,200% 200%,200% 200%,200% 200%,100% 100%;
                    animation: dvAurora 18s ease-in-out infinite;
                    filter: saturate(1.15);
                }
                @keyframes dvAurora {
                    0%   { background-position: 0% 50%,100% 50%,50% 100%,50% 0%,0% 0%; }
                    50%  { background-position: 100% 50%,0% 50%,50% 0%,50% 100%,0% 0%; }
                    100% { background-position: 0% 50%,100% 50%,50% 100%,50% 0%,0% 0%; }
                }
                /* Drifting twinkle sparkles */
                body::after {
                    content:""; position:fixed; inset:0; z-index:-1; pointer-events:none;
                    background-image:
                        radial-gradient(2px 2px at 12% 22%, #ffffff, transparent),
                        radial-gradient(2px 2px at 78% 16%, #c7d2fe, transparent),
                        radial-gradient(3px 3px at 64% 60%, #a5f3fc, transparent),
                        radial-gradient(2px 2px at 32% 72%, #fbcfe8, transparent),
                        radial-gradient(2px 2px at 88% 78%, #ffffff, transparent),
                        radial-gradient(2px 2px at 46% 34%, #ddd6fe, transparent);
                    background-repeat: no-repeat;
                    animation: dvTwinkle 5s ease-in-out infinite;
                }
                @keyframes dvTwinkle { 0%,100%{opacity:.35} 50%{opacity:.9} }

                /* Slow-drifting science emoji in the background */
                .dv-emoji { position:fixed; inset:0; z-index:-1; pointer-events:none; overflow:hidden; }
                .dv-emoji span {
                    position:absolute; bottom:-60px; font-size:34px; opacity:.18;
                    filter: drop-shadow(0 4px 8px rgba(30,58,138,.15));
                    animation: dvRise linear infinite;
                }
                @keyframes dvRise {
                    0%   { transform: translateY(0) rotate(0deg); opacity:0; }
                    10%  { opacity:.22; }
                    90%  { opacity:.22; }
                    100% { transform: translateY(-112vh) rotate(40deg); opacity:0; }
                }

                /* Challenge headline — solid black, fun rounded font */
                .dv-holo-title {
                    display:block; text-align:center; line-height:1.15; margin-bottom:16px;
                    font-family:'Fredoka', system-ui, sans-serif; font-weight:700; font-size:27px;
                    color:#000000;
                }

                .dv-stage {
                    position:relative; width:100%; max-width:1180px;
                    min-height:600px; display:flex; align-items:center;
                    justify-content:center; margin:0 auto;
                }
                /* Soft stage spotlight behind the login card */
                .dv-stage::before {
                    content:""; position:absolute; left:50%; top:50%;
                    width:560px; height:560px; transform:translate(-50%,-50%);
                    border-radius:50%; z-index:0; pointer-events:none;
                    background: radial-gradient(circle, rgba(129,140,248,0.28), transparent 68%);
                    animation: dvHalo 6s ease-in-out infinite;
                }
                .dv-floats { position:absolute; inset:0; pointer-events:none; z-index:1; }
                .dv-float {
                    position:absolute; width:250px; height:250px; pointer-events:auto;
                    filter: drop-shadow(0 18px 30px rgba(23,37,84,0.25));
                    animation: dvFloat 7s ease-in-out infinite, dvPop .7s cubic-bezier(.2,1.3,.4,1) both;
                }
                /* Vibrant pulsing glow halo behind each model (color set per-model) */
                .dv-float::before {
                    content:""; position:absolute; inset:-8%; z-index:-1; border-radius:50%;
                    background: radial-gradient(circle, var(--dv-glow,#60a5fa) 0%, transparent 68%);
                    opacity:.55; filter: blur(16px);
                    animation: dvHalo 4.5s ease-in-out infinite;
                }
                @keyframes dvHalo {
                    0%,100% { opacity:.45; transform:scale(.92); }
                    50%     { opacity:.85; transform:scale(1.1); }
                }
                .dv-float model-viewer {
                    width:100%; height:100%; background:transparent;
                    --poster-color:transparent; --progress-bar-color:#6366f1;
                }
                /* Big heart anchors the LEFT; the other three cascade down the RIGHT
                   (top → around the card). */
                .dv-tl { /* Heart — extra large, centered on the left */
                         width:440px; height:440px; top:calc(50% - 220px); left:-4%;
                         animation-delay:0s,.05s; animation-duration:8.5s,.7s;
                         animation-name: dvDrift, dvPop; }
                .dv-tr { /* Graphite — top-right */
                         width:185px; height:185px; top:-1%; right:7%;
                         animation-delay:.7s,.12s; animation-duration:6.5s,.7s; }
                .dv-bl { /* Pendulum — mid-right, pushed out */
                         width:185px; height:185px; top:33%; right:-3%;
                         animation-delay:1.4s,.20s; animation-duration:7.5s,.7s; }
                .dv-br { /* Brain — bottom-right, big */
                         width:255px; height:255px; bottom:-4%; right:6%;
                         animation-delay:2.1s,.28s; animation-duration:9.5s,.7s;
                         animation-name: dvDrift, dvPop; }
                /* Persistent tilts (on the inner viewer so the float anim is unaffected) */
                .dv-tl model-viewer { transform: rotate(-7deg); }
                .dv-tr model-viewer { transform: rotate(6deg); }
                .dv-bl model-viewer { transform: rotate(-4deg); }
                .dv-br model-viewer { transform: rotate(5deg); }
                @keyframes dvFloat {
                    0%,100% { transform: translateY(0) rotate(0deg); }
                    50%     { transform: translateY(-16px) rotate(.5deg); }
                }
                @keyframes dvDrift {
                    0%,100% { transform: translate(0,0) rotate(0deg); }
                    50%     { transform: translate(12px,-15px) rotate(-.7deg); }
                }
                @keyframes dvPop {
                    0%   { opacity:0; transform: translateY(20px) scale(.6); }
                    100% { opacity:1; transform: translateY(0) scale(1); }
                }
                /* Tablet: pull models inboard so they still frame the card */
                @media (max-width: 1180px) {
                    .dv-tl { width:300px; height:300px; top:calc(50% - 150px); left:-7%; }
                    .dv-tr { width:140px; height:140px; top:-1%; right:2%; }
                    .dv-bl { width:140px; height:140px; top:34%; right:-5%; }
                    .dv-br { width:195px; height:195px; bottom:-3%; right:1%; }
                }
                /* Phone: stop floating, flow into a centered wrapped row above the card */
                @media (max-width: 860px) {
                    .dv-stage { flex-direction:column; min-height:0 !important; }
                    .dv-floats {
                        position:static; inset:auto; order:-1; pointer-events:auto;
                        display:flex; flex-wrap:wrap; justify-content:center;
                        gap:10px; margin-bottom:14px;
                    }
                    .dv-float {
                        position:static; width:46%; max-width:170px; height:140px;
                        animation: dvPop .6s cubic-bezier(.2,1.3,.4,1) both;
                        filter:drop-shadow(0 10px 18px rgba(23,37,84,0.2));
                    }
                }
            `;
            document.head.appendChild(styleEl);
        }

        // Floating science emoji drifting up in the background
        if (!document.getElementById("dv-emoji-layer")) {
            const layer = document.createElement("div");
            layer.id = "dv-emoji-layer";
            layer.className = "dv-emoji";
            const emojis = ["🔬", "⚗️", "🧪", "⭐", "🚀", "💡", "🧬", "🪐", "✨", "🔭"];
            const html = [];
            for (let i = 0; i < emojis.length; i++) {
                const left = 5 + (i * 9.3) % 90;          // spread across width
                const dur = 16 + (i % 5) * 4;              // 16–32s rise
                const delay = (i * 2.3) % 14;              // staggered starts
                const size = 26 + (i % 4) * 8;             // 26–50px
                html.push(
                    `<span style="left:${left}%;animation-duration:${dur}s;animation-delay:-${delay}s;font-size:${size}px">${emojis[i]}</span>`
                );
            }
            layer.innerHTML = html.join("");
            document.body.appendChild(layer);
        }

        const renderShowcase = () => {
            const host = modelShowcaseRef.current;
            if (!host) return;
            host.innerHTML = DAVINCI_MODELS.map((m) => {
                const uri = resolveModelUri(m.mod);
                if (!uri) return "";
                return `
                <div class="dv-float ${m.cls}" style="--dv-glow:${m.glow || "#60a5fa"}">
                    <model-viewer
                        src="${uri}"
                        camera-controls
                        auto-rotate
                        autoplay
                        rotation-per-second="24deg"
                        interaction-prompt="none"
                        shadow-intensity="0.7"
                        exposure="1.1"
                        environment-image="neutral"
                        loading="eager"
                        reveal="auto"
                        ar="false"
                        touch-action="pan-y">
                    </model-viewer>
                </div>`;
            }).join("");
        };

        if (window.customElements && window.customElements.get("model-viewer")) {
            renderShowcase();
        } else if (!document.getElementById("dv-model-viewer-script")) {
            const script = document.createElement("script");
            script.id = "dv-model-viewer-script";
            script.type = "module";
            script.src = "https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js";
            script.onload = renderShowcase;
            document.body.appendChild(script);
        } else {
            window.customElements
                ?.whenDefined("model-viewer")
                .then(renderShowcase)
                .catch(() => {});
        }
    }, [partner]);

    useEffect(() => {
    if (prefill) {
        setUsername(normalizeEmail(prefill) || prefill);
        setPassword("");       // clear any remembered password
        setRememberMe(false);  // avoid overwriting it back
        if (partner) {
            setDavinciExistingUser(true);
            setDavinciPasswordMode(true);
        }
    }
    if (toast) setBanner(toast);
    }, [prefill, toast, partner]);

    const { t } = useTranslation();

    const handleGoogleResponse = async (response) => {
        const id_token = response.credential;
        setIsLoggingIn(true);
        setError("");
        setGoogleLoading(true);

        try {
            const res = await fetch(API_LOGIN, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    google_token: id_token
                })
            });

            const { data, rawText } = await parseLoginResponse(res);

            if (res.ok && data.token && data.student) {
                await AsyncStorage.setItem("auth_token", data.token);
                await AsyncStorage.setItem("refresh_token", data.refresh_token);
                await AsyncStorage.setItem("device_id", data.device_id);

                login(data.token, data.student);
            } else {
                setError(
                    extractLoginErrorMessage(data) ||
                    rawText ||
                    res.statusText ||
                    "Google login failed"
                );
            }
            setIsLoggingIn(false);
            setGoogleLoading(false);

        } catch (err) {
            console.error("Google login error:", err);
            setError(formatNetworkErrorMessage(err));
            setIsLoggingIn(false);
            setGoogleLoading(false);
        }
    };


    const handleLogin = async () => {
        if (!canSubmitLogin) {
            return;
        }
        if (isLoggingIn) {
            console.warn("Duplicate login attempt blocked");
            return;
        }

        setIsLoggingIn(true);
        setError("");
        setLoading(true);

        try {
            // console.log("✉️  LOGIN →", API_LOGIN, { username, password });

            const response = await fetch(API_LOGIN, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    username,
                    password,
                    ...(partner ? { science_center: "da_vinci" } : {}),
                })
            });
            // console.log("📥 response status", response.status);

            const { data, rawText } = await parseLoginResponse(response);
            // console.log("Login response data:", data);

            if (response.ok && data.token && data.student) {
                const token = data.token;
                const refreshToken = data.refresh_token;
                const deviceId = data.device_id;

                if (rememberMe) {
                    await AsyncStorage.setItem("remembered_username", username);
                    await AsyncStorage.setItem("remembered_password", password);
                } else {
                    await AsyncStorage.removeItem("remembered_username");
                    await AsyncStorage.removeItem("remembered_password");
                }

                // Store authentication data
                await AsyncStorage.setItem("auth_token", token);
                await AsyncStorage.setItem("refresh_token", refreshToken);
                await AsyncStorage.setItem("device_id", deviceId);
                await AsyncStorage.setItem("previous_access_token", token);

                login(token, data.student);

            } else {
                const extractedMessage = extractLoginErrorMessage(data);
                const fallbackMessage =
                    extractedMessage ||
                    rawText ||
                    response.statusText ||
                    "";
                const detailText = fallbackMessage.toLowerCase();
                if (detailText.includes("account not verified yet")) {
                    throw new Error("ACCOUNT_NOT_VERIFIED");
                }
                throw new Error(fallbackMessage || t(DEFAULT_LOGIN_ERROR));
            }
        } catch (error) {
            if (String(error?.message || "").includes("ACCOUNT_NOT_VERIFIED")) {
                setError("Email address not yet verified");
            } else {
                setError(
                    isNetworkFailure(error)
                        ? t("No internet connection. Please check your network and try again.")
                        : formatNetworkErrorMessage(error) || t(DEFAULT_LOGIN_ERROR)
                );
            }
            console.error("LOGIN ERR - full error object:", error);
            if (error.response) {
                console.error("server responded:", {
                    status: error.response.status,
                    data: error.response.data,
                });
            } else {
                console.error("no response from server - network error or CORS issue? message:", error.message);
                console.error("request config was:", error.config);
            }
        } finally {
            setLoading(false);
            setIsLoggingIn(false);
        }
    };

    const handleDavinciEmailContinue = async () => {
        if (!partner || davinciPasswordMode || davinciSubmitting) {
            return;
        }

        const email = normalizeEmail(username);
        if (!email) {
            setError("Email is required.");
            setDavinciMessage("");
            return;
        }

        setError("");
        setDavinciMessage("");
        setDavinciSubmitting(true);

        try {
            const response = await fetch(API_DAVINCI_EMAIL_REGISTER, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            const { data, rawText } = await parseLoginResponse(response);
            if (!response.ok) {
                const message =
                    extractLoginErrorMessage(data) ||
                    rawText ||
                    response.statusText ||
                    "Unable to continue with email";
                throw new Error(message);
            }

            setUsername(email);
            const existingUser = Boolean(data?.existing_user);
            setDavinciExistingUser(existingUser);
            setDavinciMessage(
                existingUser
                    ? "Verification link sent. Check your email to log in."
                    : "Account created. Check your email for the verification link and password."
            );
        } catch (err) {
            setError(formatNetworkErrorMessage(err));
        } finally {
            setDavinciSubmitting(false);
        }
    };

    const switchDavinciToPasswordLogin = () => {
        setDavinciPasswordMode(true);
        setDavinciMessage("");
        setError("");
    };

    // Shared login card — rendered standalone normally, or wrapped by the
    // Da Vinci 3D "stage" (with models floating around it) in partner mode.
    const loginCard = (
        <View style={[styles.loginBox, partner && styles.loginBoxPartner]}>
            {partner ? (
                <span className="dv-holo-title">{t(partner.loginTitle)}</span>
            ) : (
                <Text style={styles.title}>{t("Login to 3D AI Tutor")}</Text>
            )}
            <TextInput
                style={styles.input}
                placeholder={partner ? t("Email") : t("Email or Login ID")}
                value={username}
                onChangeText={(text) => {
                    setUsername(normalizeEmail(text));
                    if (partner) {
                        if (!davinciPasswordMode) {
                            setDavinciExistingUser(false);
                        }
                        setError("");
                        setDavinciMessage("");
                    }
                }}
                autoCapitalize="none"
                autoCorrect={false}
            />
            {(!partner || davinciPasswordMode) ? (
                <>
                    <View style={styles.passwordWrap}>
                        <TextInput
                            style={styles.passwordInput}
                            placeholder={t("Password")}
                            secureTextEntry={!showPassword}
                            value={password}
                            onChangeText={setPassword}
                        />
                        <TouchableOpacity
                            style={styles.eyeButton}
                            onPress={() => setShowPassword((v) => !v)}
                        >
                            <MaterialIcons
                                name={showPassword ? "visibility-off" : "visibility"}
                                size={20}
                                color="#6b7280"
                            />
                        </TouchableOpacity>
                    </View>

                    <TouchableOpacity
                        style={styles.rememberContainer}
                        onPress={() => setRememberMe(!rememberMe)}
                        activeOpacity={0.8}
                    >
                        <View style={[styles.checkbox, rememberMe && styles.checkboxChecked]}>
                            {rememberMe ? (
                                <MaterialIcons name="check" size={14} color="#ffffff" />
                            ) : null}
                        </View>
                        <Text style={styles.rememberLabel}>{t("Remember Me")}</Text>
                    </TouchableOpacity>

                    <Button
                        title={loading ? t("Checking...") : t("Login")}
                        onPress={handleLogin}
                        color="#2563eb"
                        disabled={!canSubmitLogin || loading || isLoggingIn}
                    />
                </>
            ) : (
                <Button
                    title={davinciSubmitting ? "Sending link..." : "Continue with email"}
                    onPress={handleDavinciEmailContinue}
                    color="#2563eb"
                    disabled={!normalizeEmail(username) || davinciSubmitting}
                />
            )}
            {loading && (
                <ActivityIndicator
                    size="small"
                    color="#2563eb"
                    style={{ marginTop: 10 }}
                />
            )}
            {error ? <Text style={styles.error}>{error}</Text> : null}
            {partner && davinciMessage ? <Text style={styles.success}>{davinciMessage}</Text> : null}

            {/* Forgot Password & Signup */}
            <View style={styles.linksContainer}>
                {partner ? (
                    davinciExistingUser && !davinciPasswordMode ? (
                        <TouchableOpacity onPress={switchDavinciToPasswordLogin}>
                            <Text style={[styles.linkText, styles.partnerLinkText]}>Didn&apos;t receive verification link? Use password instead.</Text>
                        </TouchableOpacity>
                    ) : (
                        <TouchableOpacity onPress={() => navigation.navigate("ForgotPassword")}>
                            <Text style={styles.linkText}>{t("Forgot Password?")}</Text>
                        </TouchableOpacity>
                    )
                ) : (
                    <>
                        <TouchableOpacity onPress={() => navigation.navigate("ForgotPassword")}>
                            <Text style={styles.linkText}>{t("Forgot Password?")}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => navigation.navigate("SelfRegister")} style={styles.extraLink}>
                            <Text style={styles.linkText}>Register for free trial</Text>
                        </TouchableOpacity>
                    </>
                )}
            </View>

            {!partner && (
                <>
                    <Text style={styles.orText}>OR</Text>
                    <View style={styles.googleContainer}>
                        <div ref={googleButtonRef}></div>
                        {googleLoading && (
                            <ActivityIndicator
                                size="small"
                                color="#4285F4"
                                style={{ marginTop: 10 }}
                            />
                        )}
                    </View>
                </>
            )}
        </View>
    );

    return (
        <ScrollView contentContainerStyle={[styles.container, partner && styles.containerPartner]}>
            {partner ? (
                /* BDD 2026-06-03 — Partner co-branded challenge hero */
                <>
                    {/* Logos */}
                    <View style={styles.coBrand}>
                        <View style={styles.logoRow}>
                            {partner.logo ? (
                                <Image source={partner.logo} style={styles.partnerLogo} />
                            ) : (
                                /* Placeholder until the real partner logo PNG is added (see PARTNER_BRANDS) */
                                <View style={styles.partnerLogoPlaceholder}>
                                    <Text style={styles.partnerLogoText}>{partner.name}</Text>
                                </View>
                            )}
                            <Image
                                source={require("../assets/Saras-3D_Logo.png")}
                                style={styles.sarasLogo}
                            />
                        </View>

                        {/* Challenge badge */}
                        <View style={styles.challengeBadge}>
                            <MaterialIcons name="emoji-events" size={16} color="#92400e" />
                            <Text style={styles.challengeBadgeText}>{t("DA VINCI SCIENCE CHALLENGE")}</Text>
                        </View>
                    </View>

                    {/* Certificate reward banner */}
                    <View style={[styles.certBanner, partner && styles.certBannerPartner]}>
                        <View style={styles.certIconWrap}>
                            <MaterialIcons name="workspace-premium" size={30} color="#b45309" />
                        </View>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.certTitle}>
                                {t("Finish the science challenge, earn your certificate")}
                            </Text>
                            <Text style={styles.certSubtitle}>
                                {t("Complete the Da Vinci Science Challenge to unlock your official certificate of completion.")}
                            </Text>
                        </View>
                    </View>

                    {/* Day 1 · 2 · 3 steps 
                    <View style={styles.stepsRow}>
                        {[
                            { n: "1", icon: "explore", label: t("Explore") },
                            { n: "2", icon: "view-in-ar", label: t("Build in 3D") },
                            { n: "3", icon: "military-tech", label: t("Master it") },
                        ].map((s, i) => (
                            <React.Fragment key={s.n}>
                                {i > 0 ? <View style={styles.stepConnector} /> : null}
                                <View style={styles.step}>
                                    <View style={styles.stepCircle}>
                                        <MaterialIcons name={s.icon} size={18} color="#0369a1" />
                                    </View>
                                    <Text style={styles.stepDay}>{t("Day")} {s.n}</Text>
                                    <Text style={styles.stepLabel}>{s.label}</Text>
                                </View>
                            </React.Fragment>
                        ))}
                    </View>*/}
                </>
            ) : (
                /* Marketing Banner */
                <View style={styles.banner}>
                    <Image source={require("../assets/ai_tutor_banner.png")} style={styles.bannerImage} />
                    <Text style={styles.bannerText}>
                        {t("Experience the Power of AI & 3D Hands-on Learning!")}
                    </Text>
                </View>
            )}

            {banner ? (
            <View style={{ backgroundColor: "#e8f7e8", borderColor: "#bfe8bf", borderWidth: 1, padding: 8, borderRadius: 6, marginBottom: 10 }}>
                <Text style={{ color: "#237a23", textAlign: "center" }}>{banner}</Text>
            </View>
            ) : null}

            {partner ? (
                /* Da Vinci stage: 3D exhibit models float around the login card */
                <div className="dv-stage">
                    <div ref={modelShowcaseRef} className="dv-floats" />
                    {loginCard}
                </div>
            ) : (
                loginCard
            )}
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    container: {
        flexGrow: 1,
        backgroundColor: "#f3f6fb",
        alignItems: "center",
        paddingVertical: 24
    },
    banner: {
        width: "100%",
        alignItems: "center",
        marginBottom: 20
    },
    // BDD 2026-06-03 — partner co-branded hero
    coBrand: {
        width: "100%",
        alignItems: "center",
        marginBottom: 14,
    },
    logoRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 40,
        marginBottom: 4,
    },
    partnerLogo: {
        width: 250,
        height: 100,
        resizeMode: "contain",
    },
    partnerLogoPlaceholder: {
        height: 80,
        minWidth: 150,
        paddingHorizontal: 18,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: "#1e3a8a",
        backgroundColor: "#ffffff",
        alignItems: "center",
        justifyContent: "center",
    },
    partnerLogoText: {
        fontSize: 22,
        fontWeight: "800",
        color: "#1e3a8a",
        letterSpacing: 1,
        textTransform: "uppercase",
        textAlign: "center",
    },
    crossMark: {
        fontSize: 26,
        fontWeight: "700",
        color: "#94a3b8",
    },
    sarasLogo: {
        width: 250,
        height: 100,
        resizeMode: "contain",
    },
    coBrandTitle: {
        fontSize: 24,
        fontWeight: "800",
        color: "#0f172a",
        textAlign: "center",
        marginBottom: 6,
    },
    coBrandSubtitle: {
        fontSize: 15,
        color: "#475569",
        textAlign: "center",
    },
    // BDD 2026-06-03 — challenge / certificate styling
    containerPartner: {
        backgroundColor: "#eef5ff",
    },
    challengeBadge: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        backgroundColor: "#fef3c7",
        borderRadius: 999,
        paddingVertical: 5,
        paddingHorizontal: 12,
        marginTop: 10,
    },
    challengeBadgeText: {
        fontSize: 12,
        fontWeight: "800",
        color: "#92400e",
        letterSpacing: 1,
    },
    certBanner: {
        width: "28%",
        minWidth: 330,
        maxWidth: 440,
        alignSelf: "center",
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        padding: 14,
        marginBottom: 10,
        backgroundColor: "#fffbeb",
        borderWidth: 1,
        borderColor: "#fcd34d",
        borderRadius: 14,
        shadowColor: "#b45309",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.12,
        shadowRadius: 8,
    },
    certIconWrap: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: "#fde68a",
        alignItems: "center",
        justifyContent: "center",
    },
    certTitle: {
        fontSize: 15,
        fontWeight: "800",
        color: "#92400e",
        marginBottom: 2,
    },
    certSubtitle: {
        fontSize: 12.5,
        color: "#a16207",
        lineHeight: 17,
    },
    stepsRow: {
        width: "28%",
        minWidth: 330,
        maxWidth: 440,
        alignSelf: "center",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "center",
        gap: 4,
        marginBottom: 18,
    },
    step: {
        width: 96,
        alignItems: "center",
    },
    stepCircle: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: "#e0f2fe",
        borderWidth: 1,
        borderColor: "#bae6fd",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 6,
    },
    stepDay: {
        fontSize: 12,
        fontWeight: "700",
        color: "#0369a1",
    },
    stepLabel: {
        fontSize: 11,
        color: "#64748b",
        textAlign: "center",
    },
    stepConnector: {
        width: 24,
        height: 2,
        backgroundColor: "#cbd5e1",
        marginTop: 19,
    },
    bannerImage: {
        width: 300,
        height: 150,
        resizeMode: "contain"
    },
    bannerText: {
        fontSize: 16,
        fontWeight: "600",
        textAlign: "center",
        color: "#334155",
        marginTop: 10
    },
    loginBox: {
        width: "28%",
        maxWidth: 440,
        minWidth: 330,
        padding: 22,
        backgroundColor: "#fff",
        borderRadius: 14,
        borderWidth: 1,
        borderColor: "#e2e8f0",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 8,
        zIndex: 2,            // sit above the floating 3D models on the Da Vinci stage
    },
    loginBoxPartner: {
        width: "36%",
        maxWidth: 560,
        minWidth: 420,
        padding: 24,
    },
    certBannerPartner: {
        width: "36%",
        minWidth: 420,
        maxWidth: 560,
    },
    title: {
        fontSize: 22,
        fontWeight: "700",
        marginBottom: 14,
        textAlign: "center"
    },
    // BDD 2026-06-04 — fun, kid-friendly challenge headline
    titleFun: {
        fontFamily: "Fredoka, system-ui, sans-serif",
        fontSize: 26,
        fontWeight: "700",
        color: "#4338ca",
        marginBottom: 16,
        textShadow: "0 2px 10px rgba(99,102,241,0.25)",
    },
    input: {
        width: "100%",
        padding: 12,
        borderWidth: 1,
        borderColor: "#cbd5e1",
        borderRadius: 8,
        marginBottom: 10,
        backgroundColor: "#fff"
    },
    passwordWrap: {
        width: "100%",
        flexDirection: "row",
        alignItems: "center",
        borderWidth: 1,
        borderColor: "#cbd5e1",
        borderRadius: 8,
        backgroundColor: "#fff",
        marginBottom: 10,
    },
    passwordInput: {
        flex: 1,
        padding: 12,
    },
    eyeButton: {
        paddingHorizontal: 10,
        paddingVertical: 8,
    },
    rememberContainer: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 12,
        alignSelf: "flex-start",
        paddingVertical: 4,
    },
    checkbox: {
        width: 22,
        height: 22,
        borderWidth: 1,
        borderColor: "#2563eb",
        marginRight: 10,
        borderRadius: 6,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#ffffff",
    },
    checkboxChecked: {
        backgroundColor: "#2563eb"
    },
    rememberLabel: {
        color: "#334155",
        fontSize: 14,
        fontWeight: "500",
    },
    error: {
        color: "#dc2626",
        marginTop: 10
    },
    success: {
        color: "#0f766e",
        marginTop: 10,
        fontWeight: "600",
    },
    linksContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        marginTop: 10,
        flexWrap: "wrap",
    },
    extraLink: {
        marginLeft: "auto",
    },
    linkText: {
        color: "#2563eb",
        fontWeight: "600",
    },
    partnerLinkText: {
        width: "100%",
        textAlign: "center",
        lineHeight: 20,
        flexShrink: 1,
    },
    orText: {
        marginTop: 14,
        textAlign: "center",
        color: "#6b7280",
        fontWeight: "600",
    },
    googleContainer: {
        marginTop: 10,
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
    },
});

// export default LoginScreen;
